import React, { ReactElement, useEffect, useState } from "react"
import styled, { css } from "styled-components"
import { theme } from "../../../theme"
import { ButtonPrimary } from "../button"
import { Check } from "@mui/icons-material"

export interface ButtonCardProps {
  value: string | number
  imageUrl?: string
  icons?: ReactElement[]
  title?: string
  description?: string
  selected?: boolean
  disabled?: boolean
  hasError?: boolean
  buttonLabel?: string
  vertical?: boolean
  onChange?: (value: boolean) => void
}

export const ButtonCard = (props: ButtonCardProps) => {
  const { imageUrl, icons, title, description, buttonLabel, vertical = true, onChange } = props

  const [isSelected, setIsSelected] = useState<boolean>(!!props.selected)
  const showIcons = !imageUrl && !!icons && icons.length > 0 && vertical

  const handleChange = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    if (onChange) {
      onChange(!isSelected)
    }
    setIsSelected(!isSelected)
  }

  useEffect(() => {
    setIsSelected(!!props.selected)
  }, [props.selected])

  return (
    <Card
      key={props.value}
      $selected={isSelected}
      $vertical={vertical}
      $hasError={props.hasError}
      $hasImage={!!imageUrl}
    >
      {!!imageUrl && <Image $url={imageUrl} />}
      {showIcons && (
        <Icons>
          {icons?.map((icon, index) => (
            <IconWrapper key={index}>{icon}</IconWrapper>
          ))}
        </Icons>
      )}
      <MainContent>
        <Title $vertical={vertical}>{title}</Title>
        <Description $vertical={vertical}>{description}</Description>
      </MainContent>
      <BottomButtonPrimary $vertical={vertical} onClick={(e) => handleChange(e)} disabled={!!props.disabled}>
        {isSelected ? <Check /> : buttonLabel}
      </BottomButtonPrimary>
    </Card>
  )
}

const getHorizontalStyle = (hasImage: boolean) => {
  return css`
    width: 100%;
    grid-auto-flow: column;
    grid-template-columns: ${hasImage ? "109px" : ""} auto max-content;
    min-height: 130px;
    font-size: ${theme.fontSize.s};
  `
}

const verticalStyle = css`
  max-width: 367px;
  grid-auto-flow: row;
  min-height: 330px;
  font-size: ${theme.fontSize.m};
  text-align: center;
  grid-template-rows: max-content;
`

const getCardBorderStyle = ($selected?: boolean, $hasError?: boolean) => {
  if ($selected) {
    return `1px solid ${theme.color.primary}`
  }
  if ($hasError) {
    return `1px solid ${theme.color.error}`
  }
  return `1px solid ${theme.color.white1}`
}

const Card = styled.div<{
  $selected: boolean
  $vertical: boolean
  $hasImage: boolean
  $hasError?: boolean
}>`
  display: grid;
  grid-gap: ${theme.spacing.l};
  padding: ${theme.spacing.lx};
  ${(p) => (p.$vertical === true ? verticalStyle : getHorizontalStyle(p.$hasImage))}
  border: ${(p) => getCardBorderStyle(p.$selected, p.$hasError)};
  outline: 1px solid ${(p) => (p.$selected === true ? theme.color.primary : "transparent")};
`

const Image = styled.div<{ $url: string }>`
  height: 82px;
  width: 109px;
  margin: 0 auto;
  background: url(${(p) => p.$url}) no-repeat center;
  background-size: contain;
`

const Icons = styled.div`
  display: grid;
  grid-gap: ${theme.spacing.m};
  justify-content: center;
  grid-auto-flow: column;
`

const IconWrapper = styled.div`
  width: 60px;
  height: 60px;
  background-color: ${theme.color.blue9};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: ${theme.color.primary};
  font-size: 24px;

  svg {
    width: 1em;
    height: 1em;
  }
`

const Title = styled.div<{ $vertical: boolean }>`
  ${(p) =>
    p.$vertical === true &&
    css`
      font-size: ${theme.fontSize.xl};
      line-height: ${theme.lineHeight.m};
      font-weight: 700;
    `}
  ${(p) =>
    p.$vertical === false &&
    css`
      font-size: ${theme.fontSize.m};
      line-height: ${theme.lineHeight.m};
    `}
`

const Description = styled.p<{ $vertical: boolean }>`
  margin: 0;
  color: ${(p) => (p.$vertical === true ? "inherit" : theme.color.black4)};
  line-height: ${(p) => (p.$vertical === true ? theme.lineHeight.m : theme.lineHeight.s)};
`

const MainContent = styled.div`
  display: grid;
  grid-gap: ${theme.spacing.l};
  grid-auto-flow: row;
  margin-bottom: auto;
`

const BottomButtonPrimary = styled(ButtonPrimary)<{ $vertical: boolean }>`
  margin-top: auto;
  margin-bottom: ${(p) => (p.$vertical === true ? "unset" : "auto")};
  min-width: ${(p) => (p.$vertical === true ? "" : `${theme.buttonSize.xl}`)};
  height: fit-content;
`

export const VerticalButtonCard = (props: ButtonCardProps) => <ButtonCard {...props} vertical={true} />
export const HorizontalButtonCard = (props: ButtonCardProps) => <ButtonCard {...props} vertical={false} />
